import React, { useState, useRef, useEffect, useCallback } from "react";
import { ArrowLeft, ChevronRight, Search, ChevronDown, Repeat2, Star, Eye, Trash2, X, FileText, AlertTriangle } from "lucide-react";
import { CompetitionRegisterModal } from "./competition-register-modal";
import { BenchmarkFooter } from "./benchmark-footer";
import { COMPETITIONS } from "./competitions";
import type { Competition } from "./competitions";
import { LeaderboardTab } from "./leaderboard-tab";
import { MySubmissionTab } from "./my-submission-tab";
import { MyTeamTab } from "./my-team-tab";

/* ── Types ─────────────────────────────────────── */

type DetailTab = "overview" | "rules" | "data" | "leaderboard" | "submission" | "myteam" | "certificate" | "shareboard";
type SidebarSection = "overview" | "description" | "evaluation" | "submitting" | "prizes" | "timeline" | "submission-format" | "partnerships";

/* ── Share Board Types & Data ─────────────────────── */

interface ShareItem {
  id: string;
  cheatsheetTitle: string;
  cheatsheetContent: string;
  competitionName: string;
  publishedAt: string;
  referenceCount: number;
  favoriteCount: number;
  isOwner: boolean;
  publisherName: string;
  publisherAvatar?: string;
}

const PLAYGROUND_CHEATSHEETS = [
  {
    id: "cs-1",
    title: "Chain-of-Thought Decomposition",
    content: `# Core Strategy: Chain-of-Thought Decomposition\n\n1. Break complex problems into atomic sub-problems\n2. Use dimensional analysis as a verification step\n3. Apply Polya's heuristic: Understand → Plan → Execute → Verify\n4. For combinatorics: always double-check overcounting via inclusion-exclusion\n5. For number theory: check small cases (n=1,2,3) before generalizing\n6. Template: "Let x = ... Suppose for contradiction that ..."`,
    createdAt: "2026-03-10T08:00:00Z",
  },
  {
    id: "cs-2",
    title: "Prompt Engineering for Math Reasoning",
    content: `## Prompt Engineering for Math Reasoning\n\nKey rules:\n- Always ask the model to show intermediate steps\n- Use "Verify your answer by substituting back"\n- For geometry: "Draw a diagram mentally and label all vertices"\n- For algebra: "Factor before expanding"\n- Avoid leading the model toward a specific answer\n- Temperature 0.2 works best for deterministic math`,
    createdAt: "2026-03-09T14:30:00Z",
  },
];

const MOCK_SHARE_ITEMS: ShareItem[] = [
  {
    id: "1",
    cheatsheetTitle: "Chain-of-Thought Decomposition",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[0].content,
    competitionName: "Mathematics Distillation v1",
    publishedAt: "2026-03-14T10:30:00Z",
    referenceCount: 24,
    favoriteCount: 12,
    isOwner: true,
    publisherName: "Alice Johnson",
    publisherAvatar: "https://images.unsplash.com/photo-1649589244330-09ca58e4fa64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0fGVufDF8fHx8MTc3MzY3NzI5Nnww&ixlib=rb-4.1.0&q=80&w=1080",
  },
  {
    id: "2",
    cheatsheetTitle: "Prompt Engineering for Math Reasoning",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[1].content,
    competitionName: "Mathematics Distillation v1",
    publishedAt: "2026-03-13T15:45:00Z",
    referenceCount: 18,
    favoriteCount: 9,
    isOwner: false,
    publisherName: "Bob Smith",
    publisherAvatar: "https://images.unsplash.com/photo-1554765345-6ad6a5417cde?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtYW4lMjBwb3J0cmFpdHxlbnwxfHx8fDE3NzM2MzQzNTF8MA&ixlib=rb-4.1.0&q=80&w=1080",
  },
];

const BORDER = "1px solid var(--brand-border)";

/* ── Detail Page Illustration ──────────────────── */

function DetailIllustration() {
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <svg viewBox="0 0 484 423" className="w-full h-full max-w-[484px]" fill="none">
        <g clipPath="url(#clip0_detail)">
          <path fillRule="evenodd" clipRule="evenodd" d="M21.2764 411.968H12.5898L27.1504 339.354H35.8369L21.2764 411.968ZM146.072 411.968H137.353L121.434 339.354H130.152L146.072 411.968ZM145.182 337.409H12.7002V329.957H145.182V337.409Z" fill="var(--brand-text, #3B0E27)"/>
          <mask id="mask0_detail" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="47" y="339" width="76" height="67">
            <path d="M122.116 405.019L47.6211 405.019L47.6211 401.457L122.116 401.457L122.116 405.019ZM122.116 401.156L47.6211 401.156L47.6211 397.79L122.116 397.79L122.116 401.156ZM122.116 397.293L47.6211 397.293L47.6211 394.122L122.116 394.122L122.116 397.293ZM122.116 393.431L47.6211 393.431L47.6211 390.455L122.116 390.455L122.116 393.431ZM122.116 389.567L47.6211 389.567L47.6211 386.787L122.116 386.787L122.116 389.567ZM122.116 385.704L47.6211 385.704L47.6211 383.12L122.116 383.12L122.116 385.704ZM122.116 381.841L47.6211 381.841L47.6211 379.452L122.116 379.452L122.116 381.841ZM122.116 377.978L47.6211 377.978L47.6211 375.784L122.116 375.784L122.116 377.978ZM122.116 374.114L47.6211 374.114L47.6211 372.117L122.116 372.117L122.116 374.114ZM122.116 370.252L47.6211 370.252L47.6211 368.449L122.116 368.449L122.116 370.252ZM122.116 366.389L47.6211 366.389L47.6211 364.782L122.116 364.782L122.116 366.389ZM122.116 362.524L47.6211 362.524L47.6211 361.113L122.116 361.113L122.116 362.524ZM122.116 358.66L47.6211 358.66L47.6211 357.446L122.116 357.446L122.116 358.66ZM122.116 354.798L47.6211 354.798L47.6211 353.778L122.116 353.778L122.116 354.798ZM122.116 350.935L47.6211 350.935L47.6211 350.111L122.116 350.111L122.116 350.935ZM122.116 347.071L47.6211 347.071L47.6211 346.443L122.116 346.443L122.116 347.071ZM122.116 343.209L47.6211 343.209L47.6211 342.776L122.116 342.776L122.116 343.209ZM122.116 339.108L122.116 339.346L47.6211 339.346L47.6211 339.108L122.116 339.108Z" fill="white"/>
          </mask>
          <g mask="url(#mask0_detail)">
            <path d="M70.6451 363.104C64.3553 373.823 67.3298 385.742 79.3459 385.742C90.1045 385.742 93.3611 376.936 92.7503 369.067C91.7644 356.368 83.5155 347.309 77.4989 341.715C77.4989 347.859 75.2331 355.286 70.6451 363.104Z" fill="var(--brand-text, #3B0E27)"/>
          </g>
          <path d="M47.6172 405.592C47.6172 397.303 54.3364 390.584 62.625 390.584H96.0849C104.374 390.584 111.093 397.303 111.093 405.592V410.907H47.6172V405.592Z" fill="var(--brand-text, #3B0E27)"/>
          <mask id="mask1_detail" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="332" y="106" width="160" height="142">
            <path d="M332.345 106.783L491.645 106.783L491.645 114.399L332.345 114.399L332.345 106.783ZM332.345 115.042L491.645 115.042L491.645 122.241L332.345 122.241L332.345 115.042ZM332.345 123.303L491.645 123.303L491.645 130.084L332.345 130.084L332.345 123.303ZM332.345 131.565L491.645 131.565L491.645 137.927L332.345 137.927L332.345 131.565ZM332.345 139.826L491.645 139.826L491.645 145.769L332.345 145.769L332.345 139.826ZM332.345 148.084L491.645 148.084L491.645 153.612L332.345 153.612L332.345 148.084ZM332.345 156.346L491.645 156.346L491.645 161.455L332.345 161.455L332.345 156.346ZM332.345 164.608L491.645 164.608L491.645 169.297L332.345 169.297L332.345 164.608ZM332.345 172.871L491.645 172.871L491.645 177.141L332.345 177.141L332.345 172.871ZM332.345 181.128L491.645 181.128L491.645 184.983L332.345 184.983L332.345 181.128ZM332.345 189.39L491.645 189.39L491.645 192.825L332.345 192.825L332.345 189.39ZM332.345 197.654L491.645 197.654L491.645 200.67L332.345 200.67L332.345 197.654ZM332.345 205.916L491.645 205.916L491.645 208.512L332.345 208.512L332.345 205.916ZM332.345 214.175L491.645 214.175L491.645 216.356L332.345 216.356L332.345 214.175ZM332.345 222.436L491.645 222.436L491.645 224.199L332.345 224.199L332.345 222.436ZM332.345 230.697L491.645 230.697L491.645 232.041L332.345 232.041L332.345 230.697ZM332.345 238.957L491.645 238.957L491.645 239.883L332.345 239.883L332.345 238.957ZM332.345 247.725L332.345 247.217L491.645 247.217L491.645 247.725L332.345 247.725Z" fill="white"/>
          </mask>
          <g mask="url(#mask1_detail)">
            <path fillRule="evenodd" clipRule="evenodd" d="M430.561 198.15C430.56 210.389 420.638 220.311 408.399 220.311C396.161 220.311 386.239 210.389 386.238 198.15V138.405H430.561V198.15Z" fill="var(--brand-text, #3B0E27)"/>
          </g>
          <path d="M415.307 230.219H411.88V238.165H406.828V230.219H403.398V220.122H415.307V230.219Z" fill="var(--brand-text, #3B0E27)"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M419.516 80.3411C426.122 84.1774 430.564 91.3286 430.564 99.5178V138.404H386.242V99.5178C386.242 91.3289 390.684 84.1775 397.29 80.3411L395.598 42.5022H421.209L419.516 80.3411Z" fill="var(--brand-text, #3B0E27)"/>
          <path d="M413.654 138.404V133.498C413.654 130.593 411.299 128.238 408.394 128.238L401.63 128.238C398.823 128.238 396.547 125.962 396.547 123.155C396.547 120.347 398.823 118.072 401.63 118.072H408.394L415.159 118.072C417.966 118.072 420.242 115.796 420.242 112.989C420.242 110.182 417.966 107.906 415.159 107.906H408.394L403.606 107.906C400.799 107.906 398.523 105.63 398.523 102.823C398.523 100.016 400.799 97.74 403.606 97.74H408.394L411.865 97.74C414.673 97.74 416.948 95.4643 416.948 92.6571C416.948 89.8499 414.673 87.5742 411.865 87.5742H410.03C409.127 87.5742 408.394 86.842 408.394 85.9389V42.502" stroke="var(--brand-bg, white)" strokeWidth="1.97632"/>
          <path d="M414.462 253.552C414.462 256.743 412.248 258.468 409.516 258.468C406.785 258.468 404.57 256.743 404.57 253.552C404.57 250.361 407.509 246.662 409.516 243.468C411.779 247.344 414.462 250.361 414.462 253.552Z" fill="var(--brand-text, #3B0E27)"/>
          <path d="M331.387 165.535L374.755 22.4784C375.847 18.8757 379.168 16.4121 382.933 16.4121H399.732C404.451 16.4121 408.277 20.238 408.277 24.9576V39.1131" stroke="var(--brand-text, #3B0E27)" strokeWidth="5.69696"/>
          <path d="M78.3984 136.533V50.9969C78.3984 46.2773 82.2244 42.4514 86.9439 42.4514H192.563C198.556 42.4514 202.687 48.4602 200.542 54.0562L182.133 102.07" stroke="var(--brand-text, #3B0E27)" strokeWidth="7.40605"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M384.888 185.919V193.769L303.395 165.125L305.85 158.139L384.888 185.919Z" fill="var(--brand-text, #3B0E27)"/>
          <rect x="300.309" y="154.067" width="12.3086" height="10.4924" transform="rotate(19.3189 300.309 154.067)" fill="var(--brand-text, #3B0E27)"/>
          <path d="M137.44 102.919L79.4688 82.4314" stroke="var(--brand-text, #3B0E27)" strokeWidth="7.40605"/>
          <mask id="mask2_detail" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="244" y="321" width="319" height="122">
            <path fillRule="evenodd" clipRule="evenodd" d="M244.383 321.006L562.414 321.006L562.414 327.671L244.383 327.671L244.383 321.006ZM244.383 329.659L562.414 329.659L562.414 335.885L244.383 335.885L244.383 329.659ZM244.383 338.311L562.414 338.311L562.414 344.1L244.383 344.1L244.383 338.311ZM244.383 346.965L562.414 346.965L562.414 352.315L244.383 352.315L244.383 346.965ZM244.383 355.618L562.414 355.618L562.414 360.53L244.383 360.53L244.383 355.618ZM244.383 364.272L562.414 364.272L562.414 368.746L244.383 368.746L244.383 364.272ZM244.383 372.924L562.414 372.924L562.414 376.961L244.383 376.961L244.383 372.924ZM244.383 381.578L562.414 381.578L562.414 385.176L244.383 385.176L244.383 381.578ZM244.383 390.233L562.414 390.233L562.414 393.393L244.383 393.393L244.383 390.233ZM244.383 398.887L562.414 398.887L562.414 401.607L244.383 401.607L244.383 398.887ZM244.383 407.539L562.414 407.539L562.414 409.824L244.383 409.824L244.383 407.539ZM244.383 416.192L562.414 416.192L562.414 418.038L244.383 418.038L244.383 416.192ZM244.383 424.846L562.414 424.846L562.414 426.252L244.383 426.252L244.383 424.846ZM244.383 433.498L562.414 433.498L562.414 434.469L244.383 434.469L244.383 433.498ZM244.383 442.683L244.383 442.15L562.414 442.15L562.414 442.683L244.383 442.683Z" fill="white"/>
          </mask>
          <g mask="url(#mask2_detail)">
            <path fillRule="evenodd" clipRule="evenodd" d="M480.704 387.519C486.713 398.152 479.03 411.318 466.817 411.318H356.329C344.238 411.318 336.542 398.389 342.308 387.76L372.03 332.97H449.884L480.704 387.519Z" fill="var(--brand-text, #3B0E27)"/>
          </g>
          <path fillRule="evenodd" clipRule="evenodd" d="M427.215 275.396V292.849L449.884 332.97H372.031L393.796 292.849V275.396L388.074 266.927H432.938L427.215 275.396Z" fill="var(--brand-text, #3B0E27)"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M94.9531 143.971H94.748V178.597C114.426 182.941 131.221 194.962 141.834 211.358H14.9609C25.5733 194.963 42.3674 182.942 62.0449 178.598V143.905L56.7168 139.659H100.075L94.9531 143.971Z" fill="var(--brand-text, #3B0E27)"/>
          <mask id="mask3_detail" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="-65" y="210" width="319" height="114">
            <path d="M-64.2695 210.195L253.762 210.195L253.762 216.86L-64.2695 216.86L-64.2695 210.195ZM-64.2695 218.849L253.762 218.849L253.762 225.074L-64.2695 225.074L-64.2695 218.849ZM-64.2695 227.499L253.762 227.499L253.762 233.289L-64.2695 233.289L-64.2695 227.499ZM-64.2695 236.154L253.762 236.154L253.762 241.505L-64.2695 241.505L-64.2695 236.154ZM-64.2695 244.808L253.762 244.808L253.762 249.72L-64.2695 249.72L-64.2695 244.808ZM-64.2695 253.462L253.762 253.462L253.762 257.936L-64.2695 257.936L-64.2695 253.462ZM-64.2695 262.113L253.762 262.113L253.762 266.15L-64.2695 266.15L-64.2695 262.113ZM-64.2695 270.768L253.762 270.768L253.762 274.365L-64.2695 274.365L-64.2695 270.768ZM-64.2695 279.423L253.762 279.423L253.762 282.582L-64.2695 282.582L-64.2695 279.423ZM-64.2695 288.076L253.762 288.076L253.762 290.797L-64.2695 290.797L-64.2695 288.076ZM-64.2695 296.729L253.762 296.729L253.762 299.014L-64.2695 299.014L-64.2695 296.729ZM-64.2695 305.382L253.762 305.382L253.762 307.228L-64.2695 307.228L-64.2695 305.382ZM-64.2695 314.035L253.762 314.035L253.762 315.441L-64.2695 315.441L-64.2695 314.035ZM-64.2695 323.658L-64.2695 322.688L253.762 322.688L253.762 323.658L-64.2695 323.658Z" fill="white"/>
          </mask>
          <g mask="url(#mask3_detail)">
            <path d="M141.832 211.358C149.471 223.16 153.907 237.229 153.907 252.334C153.907 294.038 120.1 327.846 78.3955 327.846C36.6912 327.846 2.88302 294.038 2.88281 252.334C2.88285 237.229 7.31942 223.16 14.959 211.358H141.832Z" fill="var(--brand-text, #3B0E27)"/>
          </g>
          <path d="M413.631 284.548C413.631 287.205 411.787 288.642 409.513 288.642C407.238 288.642 405.395 287.205 405.395 284.548C405.395 281.891 407.841 278.811 409.513 276.151C411.398 279.379 413.631 281.891 413.631 284.548Z" fill="var(--brand-bg, white)"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M141.635 97.9305C141.929 97.0958 142.857 96.6703 143.682 96.991L169.037 106.85C170.857 105.478 173.032 104.617 175.318 104.387C177.42 104.175 180.684 105.031 180.712 105.039L257.158 132.019H182.192L172.408 128.566C168.31 127.12 165.441 123.75 164.457 119.828L138.53 111.587C137.687 111.319 137.233 110.405 137.527 109.57L141.635 97.9305Z" fill="var(--brand-text, #3B0E27)"/>
          <mask id="mask4_detail" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="157" y="132" width="155" height="76">
            <path d="M157.229 132.019L311.93 132.019L311.93 136.075L157.229 136.075L157.229 132.019ZM157.229 136.418L311.93 136.418L311.93 140.252L157.229 140.252L157.229 136.418ZM157.229 140.817L311.93 140.817L311.93 144.429L157.229 144.429L157.229 140.817ZM157.229 145.217L311.93 145.217L311.93 148.606L157.229 148.606L157.229 145.217ZM157.229 149.616L311.93 149.616L311.93 152.781L157.229 152.782L157.229 149.616ZM157.229 154.015L311.93 154.015L311.93 156.958L157.229 156.958L157.229 154.015ZM157.229 158.415L311.93 158.415L311.93 161.136L157.229 161.136L157.229 158.415ZM157.229 162.815L311.93 162.815L311.93 165.312L157.229 165.312L157.229 162.815ZM157.229 167.215L311.93 167.215L311.93 169.49L157.229 169.49L157.229 167.215ZM157.229 171.614L311.93 171.614L311.93 173.666L157.229 173.666L157.229 171.614ZM157.229 176.013L311.93 176.013L311.93 177.843L157.229 177.843L157.229 176.013ZM157.229 180.414L311.93 180.414L311.93 182.021L157.229 182.021L157.229 180.414ZM157.229 184.814L311.93 184.814L311.93 186.197L157.229 186.197L157.229 184.814ZM157.229 189.213L311.93 189.213L311.93 190.374L157.229 190.374L157.229 189.213ZM157.229 193.613L311.93 193.613L311.93 194.551L157.229 194.551L157.229 193.613ZM157.229 198.012L311.93 198.012L311.93 198.727L157.229 198.727L157.229 198.012ZM157.229 202.411L311.93 202.411L311.93 202.905L157.229 202.905L157.229 202.411ZM157.229 207.08L157.229 206.81L311.93 206.81L311.93 207.08L157.229 207.08Z" fill="white"/>
          </mask>
          <g mask="url(#mask4_detail)">
            <path fillRule="evenodd" clipRule="evenodd" d="M288.036 142.912C294.533 145.205 297.94 152.33 295.647 158.827C293.354 165.324 286.229 168.732 279.732 166.439L182.207 132.019H257.171L288.036 142.912Z" fill="var(--brand-text, #3B0E27)"/>
          </g>
        </g>
        <defs>
          <clipPath id="clip0_detail">
            <rect width="483.103" height="422.145" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

/* ── Share Board Helper Functions ─────────────────────── */

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function truncateLines(text: string, maxLines: number): { text: string; truncated: boolean } {
  const lines = text.split("\n");
  if (lines.length <= maxLines) return { text, truncated: false };
  return { text: lines.slice(0, maxLines).join("\n"), truncated: true };
}

/* ── Share Board Sub-components ──────────────────────────── */

function ReferenceButton({
  referenced,
  count,
  onToggle,
  size = "sm",
}: {
  referenced: boolean;
  count: number;
  onToggle: () => void;
  size?: "sm" | "md";
}) {
  const iconSize = size === "sm" ? 14 : 16;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className="flex items-center gap-[6px] bg-transparent border-none cursor-pointer p-[6px_10px] transition-all"
      style={{
        color: referenced ? "#3B0E27" : "var(--brand-text-opacity-40)",
      }}
    >
      <Repeat2
        size={iconSize}
        fill={referenced ? "#3B0E27" : "none"}
        stroke={referenced ? "#3B0E27" : "currentColor"}
        strokeWidth={1.8}
      />
      <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]">{count}</span>
    </button>
  );
}

function FavoriteButton({
  saved,
  count,
  onToggle,
  size = "sm",
}: {
  saved: boolean;
  count: number;
  onToggle: () => void;
  size?: "sm" | "md";
}) {
  const iconSize = size === "sm" ? 14 : 16;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className="flex items-center gap-[6px] bg-transparent border-none cursor-pointer p-[6px_10px] transition-all"
      style={{
        color: saved ? "#3B0E27" : "var(--brand-text-opacity-40)",
      }}
    >
      <Star
        size={iconSize}
        fill={saved ? "#3B0E27" : "none"}
        stroke={saved ? "#3B0E27" : "currentColor"}
        strokeWidth={1.8}
      />
      <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]">{count}</span>
    </button>
  );
}

function ShareItemCard({
  item,
  liked,
  saved,
  onToggleLike,
  onToggleFavorite,
  onClick,
  onDelete,
}: {
  item: ShareItem;
  liked: boolean;
  saved: boolean;
  onToggleLike: () => void;
  onToggleFavorite: () => void;
  onClick: () => void;
  onDelete: () => void;
}) {
  const preview = truncateLines(item.cheatsheetContent, 5);

  return (
    <div
      className="flex flex-col cursor-pointer transition-colors hover:bg-[rgba(59,14,39,0.02)]"
      style={{ border: BORDER }}
      onClick={onClick}
    >
      {/* Header: title + time */}
      <div className="px-[20px] pt-[18px] pb-[12px] flex flex-col gap-[8px]">
        {/* Top row: publisher info + date */}
        <div className="flex items-center justify-between gap-[12px]">
          <div className="flex items-center gap-[8px]">
            {item.publisherAvatar ? (
              <img
                src={item.publisherAvatar}
                alt={item.publisherName}
                className="w-[20px] h-[20px] rounded-full object-cover shrink-0"
                style={{ border: "1px solid var(--brand-border)" }}
              />
            ) : (
              <div
                className="w-[20px] h-[20px] rounded-full flex items-center justify-center shrink-0 font-['Helvetica_Neue:Regular',sans-serif] text-[10px]"
                style={{
                  backgroundColor: "var(--brand-surface-muted)",
                  border: "1px solid var(--brand-border)",
                  color: "var(--brand-text-opacity-50)",
                }}
              >
                {item.publisherName.charAt(0).toUpperCase()}
              </div>
            )}
            <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0" style={{ color: "var(--brand-text-opacity-50)" }}>
              {item.publisherName}
            </p>
          </div>
          <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0 whitespace-nowrap shrink-0" style={{ color: "var(--brand-text-opacity-35)" }}>
            {formatDate(item.publishedAt)}
          </p>
        </div>

        {/* Title */}
        <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[16px] m-0" style={{ color: "var(--brand-text)" }}>
          {item.cheatsheetTitle}
        </p>
      </div>

      {/* Cheatsheet preview */}
      <div className="px-[20px] py-[0px]">
        <div
          className="p-[14px] font-['Menlo,Monaco,monospace'] whitespace-pre-wrap break-words leading-[1.65] text-[14px] h-[166px] overflow-hidden"
          style={{
            backgroundColor: "var(--brand-surface-muted)",
            border: BORDER,
            color: "var(--brand-text-opacity-60)",
          }}
        >
          {preview.text}
          {preview.truncated && (
            <span style={{ color: "var(--brand-text-opacity-30)" }}>{"\n..."}</span>
          )}
        </div>
      </div>

      {/* Actions row: View Details left, Like/Favorite right */}
      <div
        className="px-[20px] py-[10px] flex items-center justify-between mt-auto"
        style={{ borderColor: "var(--brand-border)" }}
      >
        <div className="flex items-center gap-[8px]">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="flex items-center gap-[6px] bg-transparent border-none cursor-pointer p-[4px_0]"
            style={{ color: "var(--brand-text-opacity-50)" }}
          >
            <Eye size={14} />
            <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]">View Details</span>
          </button>
          {item.isOwner && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="flex items-center gap-[5px] bg-transparent border-none cursor-pointer p-[4px_8px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px]"
              style={{ color: "#c62828" }}
            >
              <Trash2 size={12} />
              Delete
            </button>
          )}
        </div>
        <div className="flex items-center gap-[2px]">
          <ReferenceButton referenced={liked} count={item.referenceCount} onToggle={onToggleLike} />
          <FavoriteButton saved={saved} count={item.favoriteCount} onToggle={onToggleFavorite} />
        </div>
      </div>
    </div>
  );
}

/* ── Section Content Data ─────────────────────── */

const SECTIONS: { id: SidebarSection; title: string; content: string[] }[] = [
  {
    id: "overview", title: "Overview", content: [
      "The Equational Theories Challenge invites participants to push the boundaries of AI reasoning and problem-solving. This competition is designed to rigorously test the limits of modern language models and AI systems in a controlled, fair environment.",
      "The format is designed around a single submission cycle: participants prepare their solutions offline, then submit during the active window. All submissions are then evaluated uniformly using an offline process, ensuring fairness and consistency across all participants.",
      "The goal is to develop a strategy that achieves maximum accuracy while operating within strict resource constraints\u2014token limits, runtime caps, and sheet size restrictions all apply during the offline evaluation phase.",
      "Results are published simultaneously for all participants, with the latest valid submission being used for final ranking. No real-time feedback is provided during evaluation.",
    ]
  },
  {
    id: "description", title: "Description", content: [
      "Submit a universal cheat sheet to boost model reasoning across the full equation-implication bank. Participants must work within the defined constraints to develop solutions that demonstrate both accuracy and efficiency.",
      "The evaluation process tests multiple dimensions: correctness of outputs, adherence to format requirements, and computational efficiency. Scoring is primarily based on accuracy, with tiebreakers determined by submission timestamp.",
      "The problem set includes scenarios of varying difficulty, from straightforward applications to edge cases that require novel approaches. Participants are encouraged to develop robust solutions that handle the full spectrum of possible inputs.",
      "The platform provides a standardized API for submission. You may test your approach locally before the submission window opens, but all official evaluations use the competition\u2019s own infrastructure to ensure consistency.",
    ]
  },
  {
    id: "evaluation", title: "Evaluation", content: [
      "Entries for this competition will be evaluated by an automated evaluation system. The evaluation process is completely offline\u2014no network access or external API calls are permitted during evaluation.",
      "Evaluation stages after submission close include: automated format validation, correctness scoring against a hidden test set, and resource usage verification. Submissions that exceed token, runtime, or sheet limits are automatically disqualified.",
      "Detailed scoring methodology:\n\u2022 Primary score: accuracy on the hidden evaluation dataset\n\u2022 Secondary score: efficiency metrics (token usage, runtime)\n\u2022 Tiebreaker: earlier submission timestamp wins\n\u2022 Invalid submissions receive a score of zero",
    ]
  },
  {
    id: "submitting", title: "Submitting", content: [
      "To submit, participants must format their solution according to the competition\u2019s standard specification. The platform requires submissions to be uploaded through the official submission portal during the active competition window.",
      "You may submit multiple times during the submission window. Only your latest valid submission will be evaluated and ranked. Earlier submissions are automatically superseded.",
      "After saving, your submission will be queued for batch evaluation. Results from the final evaluation will be released simultaneously for all participants.",
      "Submission requirements:\n\u2022 Files must be in the specified format\n\u2022 Total submission size must not exceed the sheet limit\n\u2022 Code must be self-contained with no external dependencies",
    ]
  },
  {
    id: "prizes", title: "Prizes", content: [
      "The total prize pool for the Equational Theories Challenge is $50,000. Prizes are distributed among the top-ranking participants based on final evaluation scores.",
      "Prize distribution:\n\u2022 1st Place: 50% of prize pool\n\u2022 2nd Place: 25% of prize pool\n\u2022 3rd Place: 15% of prize pool\n\u2022 4th\u201310th Place: share remaining 10%",
      "All prize winners will also receive recognition on the Axiom Arena leaderboard and may be invited to present their approaches at the annual Axiom Arena symposium.",
    ]
  },
  {
    id: "timeline", title: "Timeline", content: [
      "The competition follows a structured lifecycle with clear phases and deadlines. All times are in UTC.",
      "Key dates:\n\u2022 Registration opens: February 1, 2026\n\u2022 Submission window: March 1\u201315, 2026\n\u2022 Evaluation period: March 16\u201325, 2026\n\u2022 Results release: March 30, 2026",
      "The competition lifecycle is designed to give participants adequate preparation time followed by a focused submission window. No extensions will be granted beyond the posted deadlines.",
      "Important: All submissions of the relevant libraries and competitions are tracked for reporting and analyzing. If requested, participants must be able to describe their methodology and verify the authenticity of their work.",
    ]
  },
  {
    id: "submission-format", title: "Submission Format Requirements", content: [
      "Although the challenge allows any type of solution architecture, submissions must follow a strict format to ensure fair offline evaluation. The accepted format involves packaging all logic into a single self-contained entry.",
      "Using existing open-source models and libraries is permitted so long as they are included within the submission bundle and do not require runtime network access.",
      "Format requirements:\n\u2022 Single entry file or bundled archive\n\u2022 Must include a standardized entry point\n\u2022 All dependencies must be vendored\n\u2022 No network access during evaluation\n\u2022 Must complete within the stated runtime limit",
    ]
  },
  {
    id: "partnerships", title: "Partnerships and Additional Resources", content: [
      "Partnering and sponsorship for the Equational Theories Challenge are managed by the organizing committee. The challenge relies on collaboration with industry partners, academic institutions, and research laboratories.",
      "Technical infrastructure has been provided by our partners to support the evaluation pipeline. Additional compute resources and dataset curation were contributed by supporting organizations.",
      "If you are interested in partnering or sponsoring future competitions, please contact the Axiom Arena organizing committee through the official website.",
    ]
  },
];

/* ── Main Detail Component ─────────────────────── */

export default function CompetitionDetail({ competitionId, onBack }: { competitionId: string; onBack: () => void }) {
  const [activeTab, setActiveTab] = useState<DetailTab>("overview");
  const [activeSection, setActiveSection] = useState<SidebarSection>("overview");
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  // Share Board state
  const [shareItems, setShareItems] = useState<ShareItem[]>(MOCK_SHARE_ITEMS);
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  const competition = COMPETITIONS.find(c => c.id === competitionId) || COMPETITIONS[0];

  const statusColors: Record<string, { bg: string; border: string; text: string }> = {
    ACTIVE: { bg: "#d8f0e3", border: "rgba(30,93,58,0.2)", text: "#1e5d3a" },
    UPCOMING: { bg: "#fff4e5", border: "rgba(185,80,0,0.2)", text: "#b95000" },
    CLOSED: { bg: "rgba(59,14,39,0.06)", border: "rgba(59,14,39,0.1)", text: "rgba(59,14,39,0.5)" },
  };

  const sc = statusColors[competition.status];

  const scrollToSection = useCallback((sectionId: SidebarSection) => {
    setActiveSection(sectionId);
    const el = sectionRefs.current[sectionId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  // Track scroll position to update active sidebar item
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY + 200;
      let current: SidebarSection = "overview";
      for (const section of SECTIONS) {
        const el = sectionRefs.current[section.id];
        if (el && el.offsetTop <= scrollTop) {
          current = section.id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Share Board handlers
  const toggleLike = (id: string) => {
    setLikedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setShareItems((items) =>
          items.map((i) => (i.id === id ? { ...i, referenceCount: i.referenceCount - 1 } : i))
        );
      } else {
        next.add(id);
        setShareItems((items) =>
          items.map((i) => (i.id === id ? { ...i, referenceCount: i.referenceCount + 1 } : i))
        );
      }
      return next;
    });
  };

  const toggleFavorite = (id: string) => {
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setShareItems((items) =>
          items.map((i) => (i.id === id ? { ...i, favoriteCount: i.favoriteCount - 1 } : i))
        );
      } else {
        next.add(id);
        setShareItems((items) =>
          items.map((i) => (i.id === id ? { ...i, favoriteCount: i.favoriteCount + 1 } : i))
        );
      }
      return next;
    });
  };

  const handleDeleteShare = (id: string) => {
    setShareItems((prev) => prev.filter((i) => i.id !== id));
  };

  const DETAIL_TABS: { id: DetailTab; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "data", label: "Data" },
    { id: "rules", label: "Benchmark" },
    { id: "shareboard", label: "Share Board" },
    { id: "leaderboard", label: "Leaderboard" },
    { id: "submission", label: "My Submission" },
    { id: "myteam", label: "My Team" },
    { id: "certificate", label: "Certificate" },
  ];

  return (
    <div className="px-[16px] md:px-[24px] pt-[16px] md:pt-[24px] pb-0 max-w-[1440px] mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-[8px] mb-[24px] md:mb-[32px]">
        <button onClick={onBack} className="bg-transparent border-none cursor-pointer p-0">
          <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] opacity-50" style={{ color: 'var(--brand-text)' }}>Competitions</span>
        </button>
        <ChevronRight size={12} style={{ color: 'var(--brand-text)', opacity: 0.5 }} />
        <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] opacity-80" style={{ color: 'var(--brand-text)' }}>{competition.title}</span>
      </div>

      {/* Hero */}
      <div className="flex flex-col lg:flex-row gap-[32px] lg:gap-[48px] items-start mb-[32px] md:mb-[48px]">
        {/* Left content */}
        <div className="flex-1 min-w-0 w-full lg:w-auto lg:max-w-[600px]">
          <h1 className="font-['Helvetica_Neue:Condensed_Bold',sans-serif] m-0 leading-[1.1] text-[32px] md:text-[40px] lg:text-[48px] tracking-[-1.8px] mb-[20px]" style={{ color: 'var(--brand-text)' }}>
            Mathematics Distillation Challenge - Equational Theories
          </h1>

          {/* Status + Meta */}
          <div className="flex flex-wrap items-center gap-[12px] mb-[24px]">
            <span
              className="inline-block px-[10px] py-[4px] font-['Helvetica_Neue:Bold',sans-serif] text-[10px] tracking-[0.7px] uppercase"
              style={{ backgroundColor: sc.bg, border: `1px solid ${sc.border}`, color: sc.text }}
            >
              {competition.status}
            </span>
            <div className="flex items-center gap-[6px]">
              <div className="size-[8px] rounded-full" style={{ backgroundColor: 'var(--brand-text)' }} />
              <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[16px] opacity-60" style={{ color: 'var(--brand-text)' }}>
                Participants: {competition.participants.toLocaleString()}
              </span>
            </div>
            <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[16px] opacity-60" style={{ color: 'var(--brand-text)' }}>
              Deadline: {competition.deadline}
            </span>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col md:flex-row gap-[12px] items-stretch md:items-center flex-wrap">
            <button
              onClick={() => {}}
              className="h-[48px] shrink-0 w-full md:w-[180px] cursor-pointer border-none"
              style={{ backgroundColor: 'var(--brand-primary-btn-bg)' }}
            >
              <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-center m-0" style={{ color: 'var(--brand-primary-btn-text)' }}>View Cheat Sheet</p>
            </button>
            <button 
              onClick={() => {}}
              className="h-[48px] shrink-0 w-full md:w-[160px] cursor-pointer bg-transparent" 
              style={{ border: '1px solid var(--brand-border-strong)' }}
            >
              <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-center m-0" style={{ color: 'var(--brand-text)' }}>Benchmark</p>
            </button>
            <button 
              onClick={() => {}}
              className="h-[48px] shrink-0 w-full md:w-[160px] cursor-pointer bg-transparent" 
              style={{ border: '1px solid var(--brand-border-strong)' }}
            >
              <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-center m-0" style={{ color: 'var(--brand-text)' }}>Playground</p>
            </button>
            <button 
              onClick={() => setActiveTab('shareboard')}
              className="h-[48px] shrink-0 w-full md:w-[180px] cursor-pointer bg-transparent" 
              style={{ border: '1px solid var(--brand-border-strong)' }}
            >
              <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-center m-0" style={{ color: 'var(--brand-text)' }}>Open Share Board</p>
            </button>
          </div>
        </div>

        {/* Right illustration */}
        <div className="flex-1 min-w-0 w-full lg:w-auto hidden lg:block">
          <div className="w-full max-w-[484px] aspect-square ml-auto">
            <DetailIllustration />
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-[24px] md:gap-[36px] overflow-x-auto pb-[2px] mb-[32px] md:mb-[40px] relative" style={{ borderBottom: '1px solid var(--brand-border-subtle)' }}>
        {DETAIL_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="bg-transparent border-none cursor-pointer pb-[12px] px-0 relative whitespace-nowrap shrink-0"
          >
            <span
              className={`font-['Helvetica_Neue:${activeTab === tab.id ? 'Medium' : 'Regular'}',sans-serif] text-[16px] ${activeTab === tab.id ? 'opacity-100' : 'opacity-70'}`}
              style={{ color: 'var(--brand-text)' }}
            >
              {tab.label}
            </span>
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ backgroundColor: 'var(--brand-text)' }} />
            )}
          </button>
        ))}
      </div>

      {/* Main Content: Sidebar + Sections */}
      {activeTab === "overview" ? (
        <div className="flex gap-[40px] md:gap-[80px] items-start">
          {/* Sidebar - desktop only */}
          <div className="hidden lg:block w-[220px] shrink-0 sticky top-[110px]">
            <div className="relative pl-[2px]">
              <div className="absolute left-0 top-0 bottom-0 w-[2px] opacity-20" style={{ backgroundColor: 'var(--brand-text)' }} />
              <div className="flex flex-col">
                {SECTIONS.map(section => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="bg-transparent border-none cursor-pointer text-left px-[16px] py-[8px] relative"
                  >
                    {activeSection === section.id && (
                      <div className="absolute left-[-2px] top-[6px] bottom-[6px] w-[2px]" style={{ backgroundColor: 'var(--brand-text)' }} />
                    )}
                    <span
                      className={`font-['Helvetica_Neue:${activeSection === section.id ? 'Medium' : 'Regular'}',sans-serif] text-[13px] tracking-[-0.08px]`}
                      style={{ color: 'var(--brand-text)', opacity: activeSection === section.id ? 1 : 0.4 }}
                    >
                      {section.title}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            {/* Back link */}
            <div className="mt-[32px] pt-[20px]" style={{ borderTop: '1px solid var(--brand-border-faint)' }}>
              <button onClick={onBack} className="bg-transparent border-none cursor-pointer p-0 flex items-center gap-[6px]">
                <ArrowLeft size={14} style={{ color: 'var(--brand-text)', opacity: 0.4 }} />
                <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] opacity-40" style={{ color: 'var(--brand-text)' }}>All Competitions</span>
              </button>
            </div>
          </div>

          {/* Content sections */}
          <div className="flex-1 min-w-0">
            {SECTIONS.map((section, si) => (
              <div
                key={section.id}
                ref={(el) => { sectionRefs.current[section.id] = el; }}
                className="mb-[40px] md:mb-[56px] scroll-mt-[120px]"
              >
                {/* Section heading with border */}
                <div className="pb-[4px] mb-[20px]" style={{ borderBottom: '1px solid var(--brand-border-subtle)' }}>
                  <h2 className="font-['Helvetica_Neue:Medium',sans-serif] text-[24px] tracking-[-0.5px] m-0" style={{ color: 'var(--brand-text)' }}>
                    {section.title}
                  </h2>
                </div>
                {/* Paragraphs */}
                <div className="flex flex-col gap-[16px]">
                  {section.content.map((paragraph, pi) => (
                    <p
                      key={pi}
                      className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] leading-[1.8] tracking-[-0.25px] m-0 whitespace-pre-line"
                      style={{ color: 'var(--brand-text)', opacity: 0.65 }}
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}

            {/* Footer disclaimer */}
            <div className="pt-[25px] mb-[40px]" style={{ borderTop: '1px solid var(--brand-border-subtle)' }}>
              <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] leading-[1.7] opacity-35 m-0" style={{ color: 'var(--brand-text)' }}>
                Please review all competition rules before submitting. &copy; 2026 Equational Theories Challenge. Rules remain valid until the announced competition end date. Axiom Arena reserves the right to adjust evaluation criteria with advance notice.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {activeTab === "leaderboard" ? (
            <LeaderboardTab />
          ) : activeTab === "submission" ? (
            <MySubmissionTab />
          ) : activeTab === "myteam" ? (
            <MyTeamTab />
          ) : activeTab === "shareboard" ? (
            <div className="pb-[40px]">
              {/* Results count */}
              <p
                className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0 mb-[16px]"
                style={{ color: "var(--brand-text-opacity-35)" }}
              >
                {shareItems.length} {shareItems.length === 1 ? "item" : "items"}
              </p>

              {/* Card Grid */}
              {shareItems.length > 0 ? (
                <div className="grid grid-cols-1 min-[1200px]:grid-cols-2 min-[1920px]:grid-cols-3 gap-[16px]">
                  {shareItems.map((item) => (
                    <ShareItemCard
                      key={item.id}
                      item={item}
                      liked={likedIds.has(item.id)}
                      saved={savedIds.has(item.id)}
                      onToggleLike={() => toggleLike(item.id)}
                      onToggleFavorite={() => toggleFavorite(item.id)}
                      onClick={() => {}}
                      onDelete={() => handleDeleteShare(item.id)}
                    />
                  ))}
                </div>
              ) : (
                <div
                  className="flex flex-col items-center justify-center py-[80px] gap-[16px]"
                  style={{ border: BORDER }}
                >
                  <div
                    className="size-[56px] flex items-center justify-center"
                    style={{ backgroundColor: "var(--brand-surface-muted)" }}
                  >
                    <FileText size={24} style={{ color: "var(--brand-text-opacity-30)" }} />
                  </div>
                  <p
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[16px] m-0"
                    style={{ color: "var(--brand-text)" }}
                  >
                    No public cheatsheets yet
                  </p>
                  <p
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] m-0"
                    style={{ color: "var(--brand-text-opacity-40)" }}
                  >
                    There are no published cheatsheets on the Share Board yet.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="py-[60px] text-center">
              <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[16px] opacity-40 m-0" style={{ color: 'var(--brand-text)' }}>
                {activeTab === "rules" && "Benchmark information will be available when the competition opens."}
                {activeTab === "data" && "Dataset information will be available when the competition opens."}
                {activeTab === "certificate" && "Certificates will be issued after the competition concludes."}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="mt-[60px] md:mt-[120px]">
        <BenchmarkFooter />
      </div>

      {/* Register Modal */}
      <CompetitionRegisterModal isOpen={showRegisterModal} onClose={() => setShowRegisterModal(false)} />
    </div>
  );
}
